import React, { useState, useEffect } from 'react';
import { X, Download, Bell, Smartphone, CheckCircle2 } from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';

export function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showNotificationStep, setShowNotificationStep] = useState(false);
  const [installSuccess, setInstallSuccess] = useState(false);
  
  const { 
    isSupported, 
    isSubscribed, 
    permission, 
    subscribe, 
    loading 
  } = usePushNotifications();

  // Check if already installed
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    setIsInstalled(isStandalone);
    
    // Don't show if already installed or dismissed recently
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const daysSinceDismiss = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      if (daysSinceDismiss < 7) return; // Don't show for 7 days after dismiss
    }
    
    // Show prompt after 60 seconds on the site (increased to not interfere with testing)
    const timer = setTimeout(() => {
      if (!isStandalone) {
        setShowPrompt(true);
      }
    }, 60000);
    
    return () => clearTimeout(timer);
  }, []);

  // Capture the beforeinstallprompt event
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handler);
    
    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setInstallSuccess(true);
      setDeferredPrompt(null);
    });
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setInstallSuccess(true);
        setShowNotificationStep(true);
      }
      setDeferredPrompt(null);
    } else {
      // For iOS or when prompt is not available
      setShowNotificationStep(true);
    }
  };

  const handleEnableNotifications = async () => {
    const success = await subscribe(false);
    if (success) {
      setTimeout(() => {
        setShowPrompt(false);
      }, 2000);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  if (!showPrompt || isInstalled) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md bg-card rounded-2xl border border-border shadow-2xl overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="relative p-6 pb-4">
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg">
              <Smartphone className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-foreground">TRADEX AI</h3>
              <p className="text-muted-foreground text-sm">Trading Intelligence</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {!showNotificationStep ? (
            <>
              <p className="text-foreground mb-6">
                Installez l&apos;app sur votre téléphone pour recevoir des <span className="text-emerald-500 dark:text-emerald-400 font-semibold">alertes de trading en temps réel</span> !
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-sm text-foreground">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>Notifications quand vos trades touchent TP1, TP2, TP100</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-foreground">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>Alertes d&apos;activation d&apos;entrée optimale</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-foreground">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>Alertes Stop Loss et liquidation</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-foreground">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>Fonctionne hors-ligne</span>
                </div>
              </div>

              {isIOS ? (
                <div className="bg-muted rounded-xl p-4 mb-4">
                  <p className="text-sm text-foreground mb-2">
                    <span className="font-semibold">Sur iPhone/iPad :</span>
                  </p>
                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Appuyez sur le bouton <span className="text-foreground font-medium">Partager</span> (carré avec flèche)</li>
                    <li>Faites défiler et appuyez sur <span className="text-foreground font-medium">&quot;Sur l&apos;écran d&apos;accueil&quot;</span></li>
                    <li>Appuyez sur <span className="text-foreground font-medium">Ajouter</span></li>
                  </ol>
                </div>
              ) : null}

              <div className="flex gap-3">
                <button
                  onClick={handleDismiss}
                  className="flex-1 px-4 py-3 rounded-xl bg-muted hover:bg-muted/80 text-foreground font-medium transition-colors"
                >
                  Plus tard
                </button>
                <button
                  onClick={handleInstall}
                  className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-medium transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25"
                >
                  <Download className="w-5 h-5" />
                  Installer
                </button>
              </div>
            </>
          ) : (
            <>
              {installSuccess && (
                <div className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium">App installée avec succès !</span>
                </div>
              )}
              
              <p className="text-foreground mb-6">
                Activez les <span className="text-emerald-500 dark:text-emerald-400 font-semibold">notifications push</span> pour ne jamais manquer une alerte de trading.
              </p>

              {permission === 'denied' ? (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4">
                  <p className="text-red-600 dark:text-red-400 text-sm">
                    Les notifications sont bloquées. Allez dans les paramètres de votre navigateur pour les autoriser.
                  </p>
                </div>
              ) : isSubscribed ? (
                <div className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium">Notifications activées !</span>
                </div>
              ) : null}

              <div className="flex gap-3">
                <button
                  onClick={handleDismiss}
                  className="flex-1 px-4 py-3 rounded-xl bg-muted hover:bg-muted/80 text-foreground font-medium transition-colors"
                >
                  {isSubscribed ? 'Fermer' : 'Plus tard'}
                </button>
                {!isSubscribed && permission !== 'denied' && (
                  <button
                    onClick={handleEnableNotifications}
                    disabled={loading || !isSupported}
                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-medium transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25 disabled:opacity-50"
                  >
                    <Bell className="w-5 h-5" />
                    {loading ? 'Activation...' : 'Activer'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PWAInstallPrompt;
